import React, { useEffect, useState } from 'react';
import { CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';

import DataTable from '../../../components/DataTable';
import Badge from '../../../components/Badge';
import Button from '../../../components/Button';
import Modal from '../../../components/Shell/Modal';
import {
	TemplateBoolean,
	TemplateField,
	TemplateReactSelect,
} from '../../../components/Attributes/Attributes_old';

import usePrevious from '../../../hooks/usePrevious';
import useAsync from '../../../hooks/useAsync';
import useRequired from '../../../hooks/useRequired';
import useModal from '../../../hooks/useModal';

import {
	addModuleFields,
	deleteModuleField,
	updateModuleField,
} from '../../../api/modules';

import { classNames } from '../../../components/helpers/functions';
import { FIELD_OPTIONS } from '../../../components/helpers/constants';
import { Input } from '../../../components/Form';

const AvailableFields = ({ module, onRefresh }) => {
	const [search, setSearch] = useState('');

	const { setOpen: setDeleteModal, setClose: closeDeleteModal } =
		useModal('deleteModal');

	const [createFieldOpen, setCreateFieldOpen] = useState(null);
	const [form, setForm] = useState({});

	const { execute: onAddModuleField, status: addingField } = useAsync(
		addModuleFields,
		{
			immediate: false,
		}
	);
	const { execute: onEditModuleField, status: editingField } = useAsync(
		updateModuleField,
		{
			immediate: false,
		}
	);

	const { execute: onDeleteModuleField, status: deletingField } = useAsync(
		deleteModuleField,
		{
			immediate: false,
		}
	);

	const availableFields = module?.attribute_fields?.filter(
		(field) => !field?.isActive
	);

	const FIELD_COLUMNS = [
		{
			header: 'Label',
			accessorKey: 'label',
			cell: (info) => {
				return (
					(
						<div>
							{info.getValue()}
							<span className="text-gray-400 text-xxs font-normal">
								{info?.row?.original?.isDefault ? ' - Default' : ''}
							</span>
						</div>
					) || null
				);
			},
		},
		{
			header: 'Key',
			accessorKey: 'key',
			cell: (info) => {
				return (
					(
						<Badge isBeta size={'xxs'}>
							{info.getValue()}
						</Badge>
					) || null
				);
			},
		},
		{
			header: 'Type',
			accessorKey: 'type',
			cell: (info) => {
				return info.getValue() || null;
			},
		},
		{
			header: 'Required',
			accessorKey: 'isRequired',
			cell: (info) => {
				return info?.row?.original?.isRequired ? (
					<CheckIcon className="w-3 h-3 text-green-600" />
				) : (
					'' || null
				);
			},
			enableSorting: false,
		},
		{
			header: 'Searchable',
			accessorKey: 'isSearchable',
			cell: (info) => {
				return info.getValue() ? (
					<CheckIcon className="w-3 h-3 text-green-600" />
				) : (
					'' || null
				);
			},
			enableSorting: false,
		},
	];

	const prevCreateFieldOpen = usePrevious(createFieldOpen);

	useEffect(() => {
		if (!createFieldOpen && prevCreateFieldOpen) {
			setForm({});
		}
	}, [createFieldOpen]);

	const hasAllRequired = useRequired(form, ['name', 'type']);

	return (
		<>
			<h2 className="text-base font-semibold leading-6 text-gray-900 mb-4 flex items-center justify-between">
				Available Fields
				<div className="p-3">
					<Button onClick={() => setCreateFieldOpen(true)}>Create</Button>
				</div>
			</h2>
			<div>
				<Input
					placeholder="Search..."
					onChange={(search) => setSearch(search?.target?.value)}
					value={search}
					PostIcon={search ? XCircleIcon : null}
					onClickPostIcon={() => setSearch('')}
				/>
			</div>
			{availableFields?.length ? (
				<ul role="list" className="divide-y divide-gray-100">
					{availableFields
						?.filter((a) => {
							return a?.label?.toLowerCase().includes(search?.toLowerCase());
						})
						?.sort((a, b) => a.label?.localeCompare(b.label))
						.map((field) => (
							<li
								key={field.id}
								className="relative flex justify-between gap-x-6 py-5  cursor-pointer"
								onClick={() => {
									setForm({
										id: field?.id,
										name: field?.label,
										type: FIELD_OPTIONS?.find((o) => o.value === field.type),
										required: field?.isRequired,
										searchable: field?.isSearchable,
									});
									setCreateFieldOpen(true);
								}}
							>
								<div className="flex min-w-0 gap-x-4 flex-1">
									{/*<img
									className="h-12 w-12 flex-none rounded-full bg-gray-50"
									src={person.imageUrl}
									alt=""
                    />*/}
									<div className="min-w-0 flex-auto flex justify-between">
										<p className="text-xs font-semibold leading-6 text-gray-900">
											{`${field?.label}${field?.isRequired ? '*' : ''}`}
										</p>
										<p className="flex text-xs leading-5 text-gray-500">
											{
												[
													...FIELD_OPTIONS,
													{ value: 'assetsList', label: 'Asset List' },
												].find((o) => o.value === field?.type)?.label
											}
										</p>
									</div>
								</div>
							</li>
						))}
				</ul>
			) : (
				<div className="flex justify-center py-10 text-gray-500 text-sm">
					No fields found
				</div>
			)}

			<Modal
				title={form?.id ? `Edit field` : 'Create new field'}
				open={createFieldOpen}
				onClose={() => setCreateFieldOpen(false)}
			>
				<div className="grid gap-y-3">
					<TemplateField
						name="Label"
						onChange={(index, name) => setForm({ ...form, name })}
						value={form?.name}
					/>
					<TemplateReactSelect
						name="Type"
						options={FIELD_OPTIONS}
						onChange={(index, type) => setForm({ ...form, type })}
						value={form?.type}
						disabled={form?.id}
					/>
					{/*<TemplateBoolean
						span={1}
						name="Required"
						className="mr-3"
						onChange={(index, required) => setForm({ ...form, required })}
						value={form?.required}
					/>*/}
					<TemplateBoolean
						span={1}
						name="Searchable"
						onChange={(index, searchable) => setForm({ ...form, searchable })}
						value={form?.searchable}
					/>

					<div
						className={classNames(
							'col-span-2 flex mt-6',
							form?.id ? 'justify-between' : 'justify-end'
						)}
					>
						{form?.id && (
							<Button
								type="danger"
								onClick={() =>
									setDeleteModal({
										title: 'Are you sure you want to delete this field?',
										description:
											'Deleting this field will delete all data created using this field.  Please make sure this is what you want to do',
										confirmText: 'Absolutely Delete',
										onConfirm: () =>
											onDeleteModuleField(
												{
													id: module?.id,
													fieldId: form?.id,
												},
												() => {
													closeDeleteModal();
													setCreateFieldOpen(false);
													onRefresh({ id: module?.id }, null, { silent: true });
												}
											),
									})
								}
								className="sm:ml-0"
							>
								Delete
							</Button>
						)}
						<Button
							disabled={!hasAllRequired}
							onClick={() => {
								form?.id
									? onEditModuleField(
											{
												id: module?.id,
												fieldId: form?.id,
												name: form?.name,
												type: form?.type?.value,
												searchable: form?.searchable,
												required: form?.required,
											},
											() => {
												setCreateFieldOpen(false);
												onRefresh({ id: module?.id }, null, { silent: true });
											}
									  )
									: onAddModuleField(
											{
												id: module?.id,
												name: form?.name,
												type: form?.type?.value,
												searchable: form?.searchable,
												required: form?.required,
												active: true,
											},
											() => {
												setCreateFieldOpen(false);
												onRefresh({ id: module?.id }, null, { silent: true });
											}
									  );
							}}
						>
							{form?.id
								? editingField === 'pending'
									? 'Saving...'
									: 'Save'
								: addingField === 'pending'
								? 'Creating...'
								: 'Create'}
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);

	return (
		<DataTable
			title="Rest of available fields"
			columns={FIELD_COLUMNS}
			data={{ rows: availableFields || [] }}
			usePagination={false}
			useExport={false}
			hasCheckboxes={false}
			manualSorting={false}
			useSearch={false}
		/>
	);
};

export default AvailableFields;
